import {
  Table,
  TableCaption,
  Thead,
  Tbody,
  Tr,
  Td,
} from "@chakra-ui/react";
import { ResidenceTypeHeaderRow } from "./ResidenceTypeHeaderRow";
import { ResidenceTypeListRow } from "./ResidenceTypeListRow";
import { useIsAuthenticated } from "@azure/msal-react";
import { useState, useCallback, useEffect, useMemo } from "react";
import {
  ResidenceTypeModel,
  getResidenceTypeList,
} from "../../clients/BackOfficeApi";
import {
  usePagination,
  withPagination,
} from "../../contexts/PaginationContext";
import { PaginationForm } from "../../elements/PaginationForm";
import { TableLoadingRow } from "../../elements/TableLoadingRow";

export const ResidenceTypeTable = withPagination(
  { settingsKey: "residenceTypes" },
  () => {
    const [residenceTypes, setResidenceTypes] = useState<ResidenceTypeModel[]>(
      [],
    );
    const isAuthenticated = useIsAuthenticated();
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const { limit, offset } = usePagination();
    const [loadedOffset, setLoadedOffset] = useState(0);
    const [tableSpan, setTableSpan] = useState(1);

    const fetchResidenceTypes = useCallback(
      (refetch = false) => {
        if (!isAuthenticated) return;

        setLoading(true);

        const abortController = new AbortController();

        const query = refetch
          ? { Offset: 0, Limit: offset + limit }
          : { Offset: offset, Limit: limit };

        getResidenceTypeList({
          query,
          signal: abortController.signal,
          throwOnError: true,
        })
          .then(({ data }) => {
            if (abortController.signal.aborted) return;

            const items = data?.items ?? [];

            const loadedItems = items.length;
            setLoadedOffset(query.Offset + loadedItems);
            setResidenceTypes((prev) => {
              prev.splice(query.Offset, loadedItems, ...items);
              return prev.slice(0, query.Offset + loadedItems);
            });
            setTotal(data?.totalCount ?? 0);
          })
          .catch((e) => {
            if (abortController.signal.aborted) return;

            throw e;
          })
          .finally(() => setLoading(false));

        return () =>
          abortController.abort("Component unmounted or re-rendered");
      },
      [isAuthenticated, limit, offset],
    );

    const moreRowsAvailable = useMemo(
      () => loadedOffset < total,
      [loadedOffset, total],
    );

    useEffect(() => fetchResidenceTypes(), [fetchResidenceTypes]);

    return (
      <Table variant="striped" w="auto">
        <TableCaption placement="top">Woningtypes ({total})</TableCaption>
        <Thead>
          <ResidenceTypeHeaderRow
            ref={(el: HTMLTableRowElement | null) =>
              setTableSpan(el?.childElementCount ?? 1)
            }
          />
        </Thead>
        <Tbody>
          <TableLoadingRow colSpan={tableSpan} loading={!moreRowsAvailable && loading} />
          {!residenceTypes.length && !loading && (
            <Tr>
              <Td colSpan={tableSpan}>Geen woningtypes gevonden</Td>
            </Tr>
          )}
          {residenceTypes.map((residenceType) => (
            <ResidenceTypeListRow
              key={residenceType.id}
              onChange={() => fetchResidenceTypes(true)}
              residenceType={residenceType}
            />
          ))}
          {moreRowsAvailable && (
            <Tr>
              <Td colSpan={tableSpan}>
                <PaginationForm loading={loading} offset={loadedOffset} />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    );
  },
);
